// THE TOPOLOGY's geometry (#6778, prototype `topo`, `:3972-4066`): where intake, the
// machines, GitHub and production sit, and where each item's dot belongs, as pure
// functions of the canvas width, the machine count and the model.
//
// Wide (700 px and up): intake left, GitHub then production right, the machines as hexagons
// between them on 5 columns past 12 machines (else 4), 540 / 480 / 430 px tall past 12 / 8 /
// fewer, hex radius at most 30. Narrow: intake on top, GitHub and production at the bottom,
// 4 columns past 9 machines (else 3), 92 px rows, radius at most 24.
//
// A dot's place reads the item's STAGE, never a simulated fraction of it (brief.md
// item 47): before "Picked up" (or with no shown machine) it circles intake; "Picked up"
// is inside its machine's hex; "Pull request" sits on the machine-to-GitHub link; "Fixed
// until green" circles GitHub; "Merged" sits on the GitHub-to-production link; "Shipped" is
// at production. A dot eases between places when a patch moves it.

import { type FleetLiveModel, idHash, type LiveItem, MACHINE_STAGE } from './fleet-live';

export const NARROW_BELOW = 700;

export type Pt = { readonly x: number; readonly y: number };

export type TopoLayout = {
  readonly w: number;
  readonly h: number;
  readonly intake: Pt;
  readonly gh: Pt;
  readonly prod: Pt;
  readonly ms: readonly Pt[];
  readonly r: number;
  /** The narrow (vertical) layout. */
  readonly v: boolean;
};

function wide(w: number, n: number): TopoLayout {
  const h = n > 12 ? 540 : n > 8 ? 480 : 430;
  const cols = n > 12 ? 5 : 4;
  const rows = Math.max(1, Math.ceil(n / cols));
  const gx0 = 190;
  const gx1 = w - 320;
  const gy0 = 50;
  const gy1 = h - 58;
  const cw = (gx1 - gx0) / cols;
  const ch = (gy1 - gy0) / rows;
  const r = Math.max(4, Math.min(30, Math.min(cw, ch) * 0.3));
  const ms = Array.from({ length: n }, (_, i) => {
    const c = i % cols;
    const row = Math.floor(i / cols);
    const skew = row % 2 ? cw * 0.16 : -cw * 0.16;
    return { x: gx0 + cw * (c + 0.5) + skew, y: gy0 + ch * (row + 0.5) };
  });
  return {
    w,
    h,
    intake: { x: 70, y: h / 2 },
    gh: { x: w - 220, y: h / 2 },
    prod: { x: w - 70, y: h / 2 },
    ms,
    r,
    v: false,
  };
}

function narrow(w: number, n: number): TopoLayout {
  const cols = n > 9 ? 4 : 3;
  const rows = Math.ceil(n / cols);
  const rowH = 92;
  const h = 150 + rows * rowH + 150;
  const cw = (w - 24) / cols;
  const r = Math.max(4, Math.min(24, cw * 0.26));
  const ms = Array.from({ length: n }, (_, i) => ({
    x: 12 + cw * ((i % cols) + 0.5),
    y: 150 + rowH * (Math.floor(i / cols) + 0.5),
  }));
  return {
    w,
    h,
    intake: { x: w / 2, y: 60 },
    gh: { x: w / 2 - 70, y: h - 70 },
    prod: { x: w / 2 + 80, y: h - 70 },
    ms,
    r,
    v: true,
  };
}

export function topoLayout(width: number, machines: number): TopoLayout {
  return width < NARROW_BELOW ? narrow(width, machines) : wide(width, machines);
}

/** A link's two control points: horizontal in the wide layout, vertical in the narrow one. */
export function controls(l: TopoLayout, a: Pt, b: Pt): [Pt, Pt] {
  if (l.v) {
    const my = (a.y + b.y) / 2;
    return [
      { x: a.x, y: my },
      { x: b.x, y: my },
    ];
  }
  const mx = (a.x + b.x) / 2;
  return [
    { x: mx, y: a.y },
    { x: mx, y: b.y },
  ];
}

/** The point at `t` along a link's cubic curve. */
export function along(l: TopoLayout, a: Pt, b: Pt, t: number): Pt {
  const [c0, c1] = controls(l, a, b);
  const u = 1 - t;
  return {
    x: u * u * u * a.x + 3 * u * u * t * c0.x + 3 * u * t * t * c1.x + t * t * t * b.x,
    y: u * u * u * a.y + 3 * u * u * t * c0.y + 3 * u * t * t * c1.y + t * t * t * b.y,
  };
}

/** Where a dot sits, and which link (if any) it travels. `hidden` sits inside a hex. */
export type DotPlace = Pt & {
  readonly kind: 'queue' | 'machine' | 'link' | 'ci' | 'node';
  /** `in<i>`, `gh<i>` or `prod`: the link a `link` dot sits on, lit while it carries one. */
  readonly link?: string;
};

/**
 * An item's place at `spin` (radians of the slow intake and CI orbits; 0 on a still frame).
 */
export function dotPlace(l: TopoLayout, item: LiveItem, spin: number): DotPlace {
  const m = item.machine >= 0 ? l.ms[item.machine] : undefined;
  const phase = ((idHash(item.id) % 1000) / 1000) * Math.PI * 2;
  if (item.state === 'set_aside') return { ...l.intake, kind: 'node' };
  if (item.stage < MACHINE_STAGE || m === undefined) {
    if (item.stage >= 5) return ciOrBeyond(l, item, phase, spin);
    const a = phase + spin * (item.intake ? 1 : -1);
    const r = 40 + (item.intake % 3) * 8;
    return {
      x: l.intake.x + Math.cos(a) * r,
      y: l.intake.y + Math.sin(a) * r * (l.v ? 0.5 : 1),
      kind: 'queue',
    };
  }
  if (item.stage === MACHINE_STAGE) return { ...m, kind: 'machine' };
  if (item.stage === MACHINE_STAGE + 1) {
    return { ...along(l, m, l.gh, 0.5), kind: 'link', link: `gh${item.machine}` };
  }
  return ciOrBeyond(l, item, phase, spin);
}

function ciOrBeyond(l: TopoLayout, item: LiveItem, phase: number, spin: number): DotPlace {
  if (item.stage === 5) {
    const o = phase + spin * 2.4;
    return { x: l.gh.x + Math.cos(o) * 34, y: l.gh.y + Math.sin(o) * 34, kind: 'ci' };
  }
  if (item.stage === 6) return { ...along(l, l.gh, l.prod, 0.5), kind: 'link', link: 'prod' };
  return { ...l.prod, kind: 'node' };
}

/** One line inside a hexagon: its text, and the item it was cut from (`null` for "IDLE"). */
export type HexLine = { readonly text: string; readonly item: LiveItem | null };

/**
 * A hexagon's inside: "IDLE", or the first two items held by that machine in `items[]`
 * order, each by its number ("#12"), or its title when it has none. Each line carries
 * its ITEM: the text is a label, never a key (two repos can each have a #12, and an ask's
 * title can read like any tag), so a line is coloured from its own item's state.
 */
export function hexLines(model: FleetLiveModel, machineId: string, idle: string): HexLine[] {
  const mine = model.items.filter((i) => i.runnerId === machineId).slice(0, 2);
  return mine.length === 0
    ? [{ text: idle, item: null }]
    : mine.map((item) => ({ text: item.tag, item }));
}
